import React, { useState, useEffect } from 'react';
import RichTextEditor from '../Common/RichTextEditor';
import api, { getImageUrl } from '../../services/api';

interface MediaFile {
  id: number;
  filename: string;
  original_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  created_at: string;
}

interface Page {
  id: number;
  title: string;
  slug: string;
  content: string;
  meta_description?: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const PageManager: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<MediaFile | null>(null);
  
  const [pageForm, setPageForm] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_published: false
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const [pagesRes, mediaRes] = await Promise.all([
        api.get('/cms/pages'),
        api.get('/cms/media')
      ]);
      setPages(pagesRes.data);
      setMediaFiles(mediaRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await api.put(`/cms/pages/${editingPage.id}`, pageForm);
      } else {
        await api.post('/cms/pages', pageForm);
      }
      setShowForm(false);
      setEditingPage(null);
      setPageForm({
        title: '',
        slug: '',
        content: '',
        meta_description: '',
        is_published: false
      });
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
    }
  };

  const handleEdit = (page: Page) => {
    setEditingPage(page);
    setPageForm({
      title: page.title,
      slug: page.slug,
      content: page.content,
      meta_description: page.meta_description || '',
      is_published: page.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (pageId: number) => {
    if (window.confirm('Are you sure you want to delete this page?')) {
      try {
        await api.delete(`/cms/pages/${pageId}`);
        fetchPages();
      } catch (error) {
        console.error('Error deleting page:', error);
      }
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (title: string) => {
    setPageForm({
      ...pageForm,
      title,
      slug: generateSlug(title)
    });
  };

  // Media Library Functions
  const openMediaLibrary = () => {
    setShowMediaLibrary(true);
  };

  const closeMediaLibrary = () => {
    setShowMediaLibrary(false);
    setSelectedMedia(null);
  };

  const selectMedia = (media: MediaFile) => {
    setSelectedMedia(media);
  };

  const insertSelectedMedia = () => {
    if (selectedMedia) {
      const imageUrl = getImageUrl(selectedMedia.file_path);
      setPageForm({
        ...pageForm, 
        content: pageForm.content + `<img src="${imageUrl}" alt="${selectedMedia.original_name}" style="max-width: 100%; height: auto;" />`
      });
      closeMediaLibrary();
    }
  };

  const filteredMediaFiles = mediaFiles.filter(media => {
    if (mediaFilter === 'all') return true;
    if (mediaFilter === 'images') return media.file_type.startsWith('image/');
    if (mediaFilter === 'documents') return media.file_type.startsWith('application/');
    return true;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const openImagePreview = (media: MediaFile) => {
    setPreviewImage(media);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImage(null);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return 'fas fa-image';
    if (fileType.includes('pdf')) return 'fas fa-file-pdf';
    if (fileType.includes('word') || fileType.includes('document')) return 'fas fa-file-word';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return 'fas fa-file-excel';
    if (fileType.includes('powerpoint') || fileType.includes('presentation')) return 'fas fa-file-powerpoint';
    if (fileType.includes('video/')) return 'fas fa-file-video';
    if (fileType.includes('audio/')) return 'fas fa-file-audio';
    return 'fas fa-file';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="cms-container">
            {showForm && (
              <div className="page-form-modal">
                <div className="modal-content" style={{ maxWidth: '900px' }}>
                  <h3>{editingPage ? 'Edit Page' : 'Create New Page'}</h3>
                  <form onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Page Title</label>
                        <input
                          type="text"
                          value={pageForm.title}
                          onChange={(e) => handleTitleChange(e.target.value)}
                          required
                          placeholder="Enter page title..."
                        />
                      </div>
                      <div className="form-group">
                        <label>URL Slug</label>
                        <input
                          type="text"
                          value={pageForm.slug}
                          onChange={(e) => setPageForm({...pageForm, slug: e.target.value})}
                          required
                          placeholder="page-url-slug"
                        />
                        <small>This will be the URL: /{pageForm.slug}</small>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label>Meta Description</label>
                      <textarea
                        value={pageForm.meta_description}
                        onChange={(e) => setPageForm({...pageForm, meta_description: e.target.value})}
                        rows={3}
                        placeholder="Brief description for search engines..."
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Page Content</label>
                      <div className="content-actions">
                        <button 
                          type="button" 
                          className="btn-browse-media"
                          onClick={openMediaLibrary}
                        >
                          <i className="fas fa-images"></i> Browse Media Library
                        </button>
                      </div>
                      <div className="editor-container">
                        <RichTextEditor
                          value={pageForm.content}
                          onChange={(content) => setPageForm({...pageForm, content})}
                          placeholder="Enter your page content here. You can use the toolbar to format text, add links, images, and more..."
                          height="400px"
                        />
                      </div>
                      {previewMode && (
                        <div className="content-preview">
                          <h4>Content Preview:</h4>
                          <div 
                            className="preview-content"
                            dangerouslySetInnerHTML={{ __html: pageForm.content }}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={pageForm.is_published}
                          onChange={(e) => setPageForm({...pageForm, is_published: e.target.checked})}
                        />
                        Publish Page (make it visible to visitors)
                      </label>
                    </div>
                    
                    <div className="form-actions">
                      <button type="submit" className="btn-primary">
                        <i className="fas fa-save"></i>
                        {editingPage ? 'Update Page' : 'Create Page'}
                      </button>
                      <button 
                        type="button" 
                        className="btn-secondary"
                        onClick={() => {
                          setShowForm(false);
                          setEditingPage(null);
                          setPageForm({
                            title: '',
                            slug: '',
                            content: '',
                            meta_description: '',
                            is_published: false
                          });
                        }}
                      >
                        <i className="fas fa-times"></i>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="cms-content">
              <div className="content-header">
                <h2>Website Pages</h2>
                <div className="page-stats">
                  <span className="stat">Total: {pages.length}</span>
                  <span className="stat">Published: {pages.filter(p => p.is_published).length}</span>
                  <span className="stat">Drafts: {pages.filter(p => !p.is_published).length}</span>
                </div>
              </div>
              
              <div className="pages-list">
                {pages.map(page => (
                  <div key={page.id} className="page-item">
                    <div className="page-info">
                      <h3>{page.title}</h3>
                      <p className="page-slug">/{page.slug}</p>
                      <p className="page-meta">{page.meta_description}</p>
                      <div className="page-meta-info">
                        <span className={`status ${page.is_published ? 'published' : 'draft'}`}>
                          {page.is_published ? 'Published' : 'Draft'}
                        </span>
                        <span className="date">
                          Updated: {new Date(page.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="page-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEdit(page)}
                      >
                        <i className="fas fa-edit"></i>
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDelete(page.id)}
                      >
                        <i className="fas fa-trash"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <div className="media-library-modal">
          <div className="modal-content" style={{ maxWidth: '900px', height: '80vh' }}>
            <div className="modal-header">
              <h3>Media Library</h3>
              <button 
                className="btn-close" 
                onClick={closeMediaLibrary}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="media-library-content">
              <div className="media-filters">
                <button 
                  className={`filter-btn ${mediaFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('all')}
                >
                  All Files
                </button>
                <button 
                  className={`filter-btn ${mediaFilter === 'images' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('images')}
                >
                  Images
                </button>
                <button 
                  className={`filter-btn ${mediaFilter === 'documents' ? 'active' : ''}`}
                  onClick={() => setMediaFilter('documents')}
                >
                  Documents
                </button>
              </div>

              <div className="media-grid">
                {filteredMediaFiles.map((media) => (
                  <div 
                    key={media.id} 
                    className={`media-item ${selectedMedia?.id === media.id ? 'selected' : ''}`}
                  >
                    {media.file_type.startsWith('image/') ? (
                      <div className="media-preview-container">
                        <img 
                          src={getImageUrl(media.file_path)} 
                          alt={media.original_name}
                          className="media-preview"
                          onClick={() => openImagePreview(media)}
                        />
                        <div className="media-overlay">
                          <button 
                            className="preview-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openImagePreview(media);
                            }}
                            title="Preview Image"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button 
                            className="select-btn"
                            onClick={() => selectMedia(media)}
                            title="Select Media"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="media-icon-container">
                        <div className="media-icon">
                          <i className={getFileIcon(media.file_type)}></i>
                        </div>
                        <div className="media-overlay">
                          <button 
                            className="select-btn"
                            onClick={() => selectMedia(media)}
                            title="Select Media"
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="media-info">
                      <div className="media-name" title={media.original_name}>{media.original_name}</div>
                      <div className="media-details">
                        <span className="media-size">{formatFileSize(media.file_size)}</span>
                        <span className="media-type">{media.file_type.split('/')[1]?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {selectedMedia && (
                <div className="media-selection">
                  <div className="selected-media-info">
                    <strong>Selected:</strong> {selectedMedia.original_name}
                  </div>
                  <div className="media-actions">
                    <button 
                      className="btn-secondary" 
                      onClick={closeMediaLibrary}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn-primary" 
                      onClick={insertSelectedMedia}
                    >
                      Insert Media
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagePreview && previewImage && (
        <div className="image-preview-modal">
          <div className="preview-modal-content">
            <div className="preview-header">
              <h3>{previewImage.original_name}</h3>
              <button 
                className="btn-close" 
                onClick={closeImagePreview}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="preview-body">
              <img 
                src={getImageUrl(previewImage.file_path)} 
                alt={previewImage.original_name}
                className="preview-image"
              />
              <div className="preview-info">
                <div className="info-row">
                  <strong>File Name:</strong> {previewImage.original_name}
                </div>
                <div className="info-row">
                  <strong>File Size:</strong> {formatFileSize(previewImage.file_size)}
                </div>
                <div className="info-row">
                  <strong>File Type:</strong> {previewImage.file_type}
                </div>
                <div className="info-row">
                  <strong>Uploaded:</strong> {new Date(previewImage.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="preview-actions">
              <button 
                className="btn-secondary" 
                onClick={closeImagePreview}
              >
                Close
              </button>
              <button 
                className="btn-primary" 
                onClick={() => {
                  selectMedia(previewImage);
                  closeImagePreview();
                }}
              >
                Select This Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageManager;
